// v2/pages/DashboardV2.js
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AccountCard from '../components/AccountCard';
import CurrencyCard from '../components/CurrencyCard';
import PurchaseSummary from '../components/PurchaseSummary';
import SavingsCard from '../components/SavingsCard';
import useAnnouncements from '../hooks/useAnnouncements';
import useAccountBalance from '../hooks/useAccountBalance';
import useExchangeRates from '../hooks/useExchangeRates';
import useProducts from '../hooks/useProducts';
import useDashboard from '../hooks/useDashboard';

import cards from '../components/StatCards.module.css';
import ProductTable from '../components/ProductTable';
import ProductDetailModal from '../components/ProductDetailModal';
import SearchNotice from '../components/SearchNotice';
import CategoryAnalytics from '../components/CategoryAnalytics';
import Announcements from '../components/Announcements';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import layout from '../styles/HomeLayout.module.css';
import styles from '../styles/DashboardV2.module.css';
import '../styles/tokens.css';

export default function DashboardV2() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const cart = useCartContext();
  const toast = useToast();
  const [detailProduct, setDetailProduct] = useState(null);

  const bakiye = useAccountBalance();
  const kurlar = useExchangeRates();
  const duyurular = useAnnouncements();
  const EUR_RATE = kurlar.eurRate;

  // Cariye ozel ana sayfa verisi (kategori, alis ozeti, kazanc, indirimler)
  const pano = useDashboard();
  const d = pano.data;

  // AccountCard yuklenirken bos kart yerine 0 gostersin diye guvenli varsayilan
  const hesapKarti = bakiye.balance || { balance: 0, lastActivity: '—' };

  // Anasayfa listesi: tum katalog, sunucu tarafli sayfalama
  const [sorgu, setSorgu] = useState({
    page: 1, perPage: 8, sort: 'stock-desc', stock: 'all', campaign: 'all',
  });
  const liste = useProducts({ ...sorgu, search: searchTerm });

  const sorguGuncelle = useCallback((yama) => {
    setSorgu((onceki) => ({ ...onceki, ...yama }));
  }, []);

  // Arama degisince ilk sayfaya don
  const aramaDegisti = useCallback((deger) => {
    setSearchTerm(deger);
    setSorgu((onceki) => (onceki.page === 1 ? onceki : { ...onceki, page: 1 }));
  }, []);

  const handleMenu = useMenuNav(toast.push);

  const handleAddToCart = useCallback((product, qty, wh) => {
    cart.add(product, qty, wh);
    toast.push(`${product.description} — ${qty} adet ${wh ? wh.label : 'sepete'} eklendi`);
  }, [cart, toast]);

  // Urunler sayfasi ?campaign=general parametresini okuyacak (urunler sayfasi gelistirmesi)
  const kampanyalariGoster = useCallback(() => {
    navigate('/v2/products?campaign=general');
  }, [navigate]);

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={searchTerm}
        onSearchChange={aramaDegisti}
        cart={cart}
        eurRate={EUR_RATE}
        onMenuSelect={handleMenu}
      />

      <main className={styles.main}>
        <div className={layout.wrap}>
          <div className={layout.left}>
            <div className={layout.topRowLeft}>
              <CategoryAnalytics
                data={d ? d.categories : null}
                loading={pano.loading}
                error={pano.error}
                onRetry={pano.yenile}
              />
            </div>

            <SearchNotice
              info={liste.aramaBilgi}
              current={searchTerm}
              onClear={() => aramaDegisti('')}
            />

            <ProductTable
              products={liste.products}
              onAddToCart={handleAddToCart}
              onOpenDetail={setDetailProduct}
              showAllLink
              title="Ürünler"
              showToolbar={false}
              showOem={false}
              simpleStock
              showActions
              initialPageSize={8}
              showPageSizer={false}
              fixedHeight
              eurRate={EUR_RATE}
              loading={liste.loading}
              serverMode
              serverPage={sorgu.page}
              serverTotalPages={liste.totalPages}
              serverTotalItems={liste.totalItems}
              onServerChange={sorguGuncelle}
            />
          </div>

          <aside className={layout.right}>
            <div className={layout.topRowRight}>
              <AccountCard data={hesapKarti} onOpenAccount={() => navigate('/v2/account')} />
            </div>
            <CurrencyCard items={kurlar.rates} asOf={kurlar.guncelTarih} />
            <Announcements items={duyurular.items} loading={duyurular.loading} />
          </aside>
        </div>

        <div className={cards.gridBottom}>
          <PurchaseSummary
            months={d ? d.purchaseMonths : []}
            year={d ? d.year : null}
            loading={pano.loading}
            error={pano.error}
            onRetry={pano.yenile}
          />
          <SavingsCard
            savings={d ? d.savings : null}
            discounts={d ? d.discounts : null}
            loading={pano.loading}
            error={pano.error}
            onRetry={pano.yenile}
            onShowCampaigns={kampanyalariGoster}
          />
        </div>
      </main>
      <Footer />

      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onAddToCart={handleAddToCart}
          eurRate={EUR_RATE}
        />
      )}
    </div>
  );
}
